import type { ProductFilters, SleepingProductFilters } from "../types";
import type {
  ProductCRMFilters,
  ProductPartnerFilters,
  ProductRollingFilters,
  ProductOrdersFilters,
} from "@/app/api/types/product";

/**
 * Product query key factory
 * Provides type-safe and hierarchical query keys for all product-related queries
 */
export const productKeys = {
  // Base key for all product data
  all: ["products"] as const,

  // All product lists (different filter combinations)
  lists: () => [...productKeys.all, "list"] as const,
  list: (filters: ProductFilters) => [...productKeys.lists(), filters] as const,

  // Individual products
  details: () => [...productKeys.all, "detail"] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,

  // Special product queries
  sleeping: (filters?: SleepingProductFilters) =>
    [...productKeys.all, "sleeping", filters || {}] as const,

  // Lookup queries
  bySku: (sku: string) => [...productKeys.all, "sku", sku] as const,
  byCode: (code: string) => [...productKeys.all, "code", code] as const,

  // Related data
  // members: (productId: string) => [...productKeys.all, 'members', productId] as const, // Not implemented in backend
  userProducts: (userId: string) =>
    [...productKeys.all, "user", userId] as const,

  // Product CRM keys
  crm: (productId: string) => [...productKeys.all, "crm", productId] as const,
  crmStatistics: (productId: string, filters: ProductCRMFilters) =>
    [...productKeys.crm(productId), "statistics", filters] as const,
  crmPartners: (productId: string, filters: ProductPartnerFilters) =>
    [...productKeys.crm(productId), "partners", filters] as const,
  crmRolling: (productId: string, filters: ProductRollingFilters) =>
    [...productKeys.crm(productId), "rolling", filters] as const,
  crmOrders: (productId: string, filters: ProductOrdersFilters) =>
    [...productKeys.crm(productId), "orders", filters] as const,

  // Product relevance (recency-weighted usage)
  relevance: () => [...productKeys.all, "relevance"] as const,
} as const;
